import { useMutation, UseMutationOptions, useQuery, useQueryClient } from '@tanstack/react-query';

import { IncomingError } from './responseTransform';

export const useApiGet = <T, E = IncomingError>(key, fn, options) =>
  useQuery<T, E>({
    queryKey: key,
    queryFn: fn,
    retry: false,
    refetchOnWindowFocus: window.env.CARE_ENVIRONMENT == 'local',
    ...options,
  });

export const useApiSend = <TData, TVariables, E = Error>(
  fn?: (variables: TVariables) => Promise<TData>,
  onSuccess?: (data: TData, variables: TVariables, context: unknown) => void,
  onError?: (error: E) => void,
  invalidateKeys?: string[],
  options?: Omit<UseMutationOptions<TData, unknown, TVariables>, 'mutationFn'>
) => {
  const queryClient = useQueryClient();

  return useMutation<TData, unknown, TVariables>({
    mutationFn: fn,
    onSuccess: (data, variables, context) => {
      invalidateKeys?.forEach((key) => {
        queryClient.invalidateQueries({ queryKey: [key] });
      });
      onSuccess?.(data, variables, context);
    },
    onError: (error) => {
      if (onError) {
        onError(error as E);
      }
    },
    ...options,
  });
};
